import { cn } from "@repo/design-system/utils";
import { X } from "lucide-react";

/**
 * One removable applied-filter chip. The `key` disambiguates chips within a
 * render (a facet value, or a price bound); `onRemove` drops just this filter.
 */
export interface AppliedChip {
  /** Stable identity for React and for tests to target. */
  key: string;
  /** Human-readable label shown in the chip, e.g. a brand name or "On sale". */
  label: string;
  /** Remove only this filter (both the InstantSearch refinement and the URL). */
  onRemove: () => void;
}

export interface AppliedFilterChipsProps {
  chips: AppliedChip[];
  /** Clear every applied filter at once. */
  onClearAll: () => void;
  className?: string;
}

/**
 * The applied-filter chips shown above the results at every breakpoint, so the
 * active filter state is visible - and each facet individually removable -
 * without opening the filter panel (the phone case, where the panel is an
 * off-canvas drawer). Presentational only: it owns no filter state, it renders
 * the chips it's handed and calls back on removal. Deriving the chips from the
 * live refinements and mapping each back to its URL param is the caller's job
 * (`SearchPage`), which is where the routing and attribute knowledge already
 * live.
 *
 * Renders nothing when there's nothing applied, so callers can mount it
 * unconditionally.
 */
export function AppliedFilterChips({
  chips,
  onClearAll,
  className
}: AppliedFilterChipsProps) {
  if (chips.length === 0) return null;

  return (
    <div
      className={cn("flex flex-wrap items-center gap-2", className)}
      // A labelled group so a screen reader announces this as the set of active
      // filters rather than a loose run of buttons.
      role="group"
      aria-label="Applied filters"
    >
      {chips.map((chip) => (
        <span
          key={chip.key}
          // `max-w-full` + a truncating label so a long, space-less facet value
          // (a brand / supplier name) can't push the chip past the results
          // column and give its scroll container a horizontal scrollbar at the
          // narrow breakpoints (the epic's no-horizontal-overflow rule).
          className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-full bg-gray-100 py-1 pl-3 pr-1 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-200"
        >
          <span className="min-w-0 truncate" title={chip.label}>
            {chip.label}
          </span>
          <button
            type="button"
            aria-label={`Remove ${chip.label} filter`}
            onClick={chip.onRemove}
            className="shrink-0 rounded-full p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        Clear all
      </button>
    </div>
  );
}
